import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useUser } from './LoginPersistence';
import { useVersionWidget } from '../contexts/VersionWidgetContext';
import { APP_VERSION, getDeployTarget, DEV_MODE } from '../config/environment';

const ENV_URLS = {
  staging: 'https://venuereportcard-staging.web.app/version.json',
  production: 'https://venue-report-card.web.app/version.json',
};

const ENV_ORDER = ['development', 'staging', 'production'];

const ENV_LABELS = {
  development: 'dev',
  staging: 'staging',
  production: 'production',
};

function getHostname() {
  if (typeof window === 'undefined' || !window.location) return '—';
  return window.location.hostname || '—';
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '—';
  } catch {
    return '—';
  }
}

function getLocalTime() {
  try {
    return new Date().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function formatDate(val) {
  if (val == null) return '—';
  try {
    const iso = typeof val === 'object' && val && typeof val.toDate === 'function'
      ? val.toDate().toISOString()
      : String(val);
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

async function fetchVersionJson(url) {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function VersionWidget() {
  const { user } = useUser();
  const { visible } = useVersionWidget();

  // currentBuild: info about THIS running build (from /version.json or env vars)
  const [currentBuild, setCurrentBuild] = useState(null);
  // remoteVersions: { staging: {version, deployedAt} | null, production: {version, deployedAt} | null }
  const [remoteVersions, setRemoteVersions] = useState({ staging: null, production: null });
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [localTime, setLocalTime] = useState(getLocalTime);

  const isAdmin = user && (user.userType === 'admin' || user.isAdmin === true);

  useEffect(() => {
    if (!visible || !isAdmin) return;
    let cancelled = false;

    const load = async () => {
      // 1. Load current build info
      const version = APP_VERSION || null;
      const target = getDeployTarget();
      const localJson = await fetchVersionJson('/version.json');
      if (!cancelled) {
        setCurrentBuild({
          version: version || (localJson && localJson.version) || null,
          deployTarget: (localJson && localJson.deployTarget) || target || 'development',
          deployedAt: (localJson && localJson.deployedAt) || null,
        });
      }

      // 2. Remote deploy versions (skip on localhost — CORS blocks cross-origin fetch)
      if (!DEV_MODE) {
        const [stagingData, productionData] = await Promise.all([
          fetchVersionJson(ENV_URLS.staging),
          fetchVersionJson(ENV_URLS.production),
        ]);
        if (!cancelled) {
          setRemoteVersions({
            staging: stagingData,
            production: productionData,
          });
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [visible, isAdmin]);

  useEffect(() => {
    if (!visible || !expanded) return;
    const t = setInterval(() => setLocalTime(getLocalTime()), 1000);
    return () => clearInterval(t);
  }, [visible, expanded]);

  if (!visible || !isAdmin) return null;

  const currentTarget = (currentBuild && currentBuild.deployTarget) || getDeployTarget();
  // On dev, APP_VERSION isn't injected — fall back to the latest known deployed version
  const latestDeployedVersion =
    (remoteVersions.staging && remoteVersions.staging.version) ||
    (remoteVersions.production && remoteVersions.production.version) ||
    null;
  const currentVersion = (currentBuild && currentBuild.version) || latestDeployedVersion;

  // Build per-env rows
  const envRows = ENV_ORDER.map((env) => {
    const isCurrentEnv = env === currentTarget;
    let version = null;
    let deployedAt = null;

    if (env === 'development') {
      // For dev, no remote URL — show current version when active, latest deployed otherwise
      version = isCurrentEnv
        ? (currentVersion || null)
        : (latestDeployedVersion || null);
      deployedAt = isCurrentEnv ? (currentBuild && currentBuild.deployedAt) : null;
    } else {
      const data = remoteVersions[env];
      if (data) {
        version = data.version;
        deployedAt = data.deployedAt;
      }
      // If we're currently on this env, supplement from currentBuild (more accurate deployedAt)
      if (isCurrentEnv && currentBuild) {
        version = version || currentVersion;
        deployedAt = deployedAt || currentBuild.deployedAt;
      }
    }

    // Dot color:
    // - current env + version matches what's deployed there → green
    // - current env + version mismatch → yellow
    // - other envs with known version → green (deployed/known)
    // - no version info → grey
    let statusColor;
    if (!version) {
      statusColor = 'rgba(255,255,255,0.25)';
    } else if (isCurrentEnv) {
      const deployedVersion = env !== 'development' && remoteVersions[env]
        ? remoteVersions[env].version
        : null;
      statusColor = (deployedVersion && currentVersion && deployedVersion === currentVersion) || env === 'development'
        ? '#4ade80'
        : '#facc15';
    } else {
      statusColor = '#4ade80';
    }

    return { env, label: ENV_LABELS[env], isCurrentEnv, version, deployedAt, statusColor };
  });

  const currentRow = envRows.find(r => r.isCurrentEnv);
  const badgeVersion = currentVersion || '?';
  const badgeLabel = DEV_MODE ? 'dev' : currentTarget;
  const badgeDotColor = currentRow ? currentRow.statusColor : 'rgba(255,255,255,0.25)';
  const badgeDotOk = badgeDotColor === '#4ade80';

  return (
    <View style={styles.wrapper} pointerEvents="box-none">
      {expanded && (
        <View style={[
          styles.panel,
          currentTarget === 'production' ? styles.panelProd
            : currentTarget === 'staging' ? styles.panelStaging
            : styles.panelDev,
        ]}>
          <Text style={styles.panelTitle}>Environments</Text>

          {envRows.map(({ env, label, isCurrentEnv, version, deployedAt, statusColor }) => (
            <View key={env} style={[styles.envRow, isCurrentEnv && styles.envRowCurrent]}>
              <Text style={[styles.envDot, { color: statusColor }]}>●</Text>
              <View style={styles.envRowContent}>
                <View style={styles.envRowHeader}>
                  <Text style={[styles.envLabel, isCurrentEnv && styles.envLabelCurrent]}>
                    {label}{isCurrentEnv ? ' ←' : ''}
                  </Text>
                  <Text style={[styles.envVersion, version ? { color: statusColor } : styles.envVersionNone]}>
                    {version ? `v${version}` : '—'}
                  </Text>
                </View>
                <Text style={styles.envDeployedAt}>
                  {deployedAt ? formatDate(deployedAt) : 'no deploy data'}
                </Text>
              </View>
            </View>
          ))}

          <View style={styles.panelDivider} />

          <Text style={styles.panelRow}>
            Host: <Text style={styles.panelValueMuted}>{getHostname()}</Text>
          </Text>
          <Text style={styles.panelRow}>
            Timezone: <Text style={styles.panelValueMuted}>{getTimezone()}</Text>
          </Text>
          <Text style={styles.panelRow}>
            Local time: <Text style={styles.panelValueMuted}>{localTime}</Text>
          </Text>
          {DEV_MODE && (
            <Text style={styles.panelRowDev}>Running in development mode</Text>
          )}
          <Text style={styles.panelHint}>Toggle in Profile → Settings</Text>
        </View>
      )}

      <TouchableOpacity
        style={[
          styles.badge,
          currentTarget === 'production' ? styles.badgeProd
            : currentTarget === 'staging' ? styles.badgeStaging
            : styles.badgeDev,
        ]}
        onPress={() => setExpanded((e) => !e)}
        activeOpacity={0.85}
      >
        <Text style={styles.badgeText} numberOfLines={1}>
          v{badgeVersion} · {badgeLabel}
        </Text>
        {!loading && (
          <Text style={[styles.badgeDot, badgeDotOk ? styles.badgeDotOk : styles.badgeDotMismatch]}>
            {' ●'}
          </Text>
        )}
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={16}
          color="rgba(255,255,255,0.7)"
          style={styles.badgeChevron}
        />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: 16,
    bottom: 16,
    zIndex: 9998,
    ...(Platform.OS === 'web' && { maxWidth: '42%' }),
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  badgeDev: {
    backgroundColor: 'rgba(20, 45, 55, 0.82)',
    borderColor: 'rgba(0, 187, 223, 0.4)',
  },
  badgeStaging: {
    backgroundColor: 'rgba(35, 40, 80, 0.82)',
    borderColor: 'rgba(100, 120, 255, 0.4)',
  },
  badgeProd: {
    backgroundColor: 'rgba(40, 35, 65, 0.82)',
    borderColor: 'rgba(160, 100, 220, 0.4)',
  },
  badgeText: {
    color: 'rgba(255,255,255,0.95)',
    fontSize: 12,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
  },
  badgeDot: {
    fontSize: 11,
    marginLeft: 6,
  },
  badgeDotOk: {
    color: '#4ade80',
  },
  badgeDotMismatch: {
    color: '#facc15',
  },
  badgeChevron: {
    marginLeft: 6,
  },
  panel: {
    marginBottom: 10,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 240,
  },
  panelDev: {
    backgroundColor: 'rgba(15, 40, 50, 0.94)',
    borderColor: 'rgba(0, 187, 223, 0.35)',
  },
  panelStaging: {
    backgroundColor: 'rgba(25, 30, 55, 0.94)',
    borderColor: 'rgba(100, 120, 255, 0.35)',
  },
  panelProd: {
    backgroundColor: 'rgba(35, 25, 55, 0.94)',
    borderColor: 'rgba(160, 100, 220, 0.35)',
  },
  panelTitle: {
    color: 'rgba(255,255,255,0.55)',
    fontWeight: '600',
    fontSize: 10,
    marginBottom: 10,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  envRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 7,
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderRadius: 5,
  },
  envRowCurrent: {
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  envDot: {
    fontSize: 9,
    marginTop: 3,
    marginRight: 8,
  },
  envRowContent: {
    flex: 1,
    minWidth: 0,
  },
  envRowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  envLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
  },
  envLabelCurrent: {
    color: 'rgba(255,255,255,0.98)',
    fontWeight: '700',
  },
  envVersion: {
    fontSize: 11,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    fontWeight: '600',
  },
  envVersionNone: {
    color: 'rgba(255,255,255,0.25)',
  },
  envDeployedAt: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: 10,
    marginTop: 2,
  },
  panelDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    marginVertical: 10,
  },
  panelRow: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 10,
    marginBottom: 3,
  },
  panelValueMuted: {
    color: 'rgba(255,255,255,0.5)',
  },
  panelRowDev: {
    color: 'rgba(0, 187, 223, 0.8)',
    fontSize: 10,
    fontStyle: 'italic',
    marginTop: 4,
  },
  panelHint: {
    color: 'rgba(255,255,255,0.28)',
    fontSize: 10,
    marginTop: 10,
  },
});

export default VersionWidget;
